import { useState } from "react";
import { Heart, HandHeart, Shield, Droplets, Home, Users, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { useDonationStats } from "@/hooks/useConflictData";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

const amounts = [5, 10, 25, 50, 100];

const impactStatements: Record<number, string> = {
  5: "Provides emergency water purification for 1 family for 2 days",
  10: "Supplies 3 days of clean water for a family of 4",
  25: "Funds a family emergency hygiene kit with essentials",
  50: "Covers 1 week of emergency food supplies for a family",
  100: "Provides a full emergency shelter kit for a displaced family",
};

const causeDetails = {
  ukraine: {
    label: "Ukraine Humanitarian Relief",
    description: "Medical supplies, shelter, and food for civilians affected by the ongoing conflict",
    affected: "6.5M+ internally displaced",
    aidType: "Medical, shelter, food",
    icon: Home,
  },
  gaza: {
    label: "Gaza Humanitarian Relief",
    description: "Clean water, emergency medical care, and shelter for civilians in Gaza",
    affected: "2M+ in need of aid",
    aidType: "Water, medical, shelter",
    icon: Droplets,
  },
};

const DonationSection = () => {
  const [selectedCause, setSelectedCause] = useState<"ukraine" | "gaza">("ukraine");
  const [selectedAmount, setSelectedAmount] = useState(25);
  const [customAmount, setCustomAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const { data: stats } = useDonationStats();
  const { toast } = useToast();

  const causes = [
    { id: "ukraine" as const, raised: (stats?.ukraine.raised || 0) + 8675, goal: stats?.ukraine.goal ?? 40000, donors: 347 },
    { id: "gaza" as const, raised: (stats?.gaza.raised || 0) + 5120, goal: stats?.gaza.goal ?? 30000, donors: 256 },
  ];

  const cause = causes.find((c) => c.id === selectedCause)!;
  const details = causeDetails[selectedCause];
  const pct = cause.goal > 0 ? Math.min(Math.round((cause.raised / cause.goal) * 100), 100) : 0;
  const finalAmount = customAmount ? parseInt(customAmount) : selectedAmount;
  const isValidAmount = finalAmount >= 5;

  const handleDonate = async () => {
    if (!isValidAmount) {
      toast({ title: "Minimum $5", description: "The minimum donation amount is $5 USD.", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("create-donation", {
        body: { amount: finalAmount, cause: selectedCause },
      });
      if (error) throw error;
      if (data?.url) window.location.href = data.url;
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const DetailIcon = details.icon;

  return (
    <section id="donate" className="border-b border-border">
      <div className="container mx-auto px-4 py-16">
        {/* Header */}
        <div className="mb-10 text-center">
          <Badge variant="outline" className="mb-4 border-danger/30 bg-danger/5 text-danger">
            <Heart className="mr-1.5 h-3 w-3" />
            Humanitarian Relief
          </Badge>
          <h2 className="mb-3 text-2xl font-bold text-foreground md:text-3xl">
            Your Donation Provides Direct Relief
          </h2>
          <p className="mx-auto max-w-xl text-sm leading-relaxed text-muted-foreground">
            100% of your donation goes to verified international humanitarian organizations working on the ground.
            <span className="font-semibold text-foreground"> Zero platform commission.</span>
          </p>
        </div>

        <div className="mx-auto max-w-2xl">
          {/* Cause Cards */}
          <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {(Object.keys(causeDetails) as Array<"ukraine" | "gaza">).map((id) => {
              const d = causeDetails[id];
              const c = causes.find(x => x.id === id)!;
              const Icon = d.icon;
              const isSelected = selectedCause === id;
              const p = c.goal > 0 ? Math.min(Math.round((c.raised / c.goal) * 100), 100) : 0;

              return (
                <button
                  key={id}
                  onClick={() => setSelectedCause(id)}
                  className={`rounded-xl border p-4 text-left transition-all ${
                    isSelected
                      ? "border-primary bg-primary/5 ring-1 ring-primary/20"
                      : "border-border bg-card hover:border-primary/30"
                  }`}
                >
                  <div className="mb-2 flex items-center gap-2">
                    <div className={`flex h-8 w-8 items-center justify-center rounded-lg ${isSelected ? "bg-primary/15" : "bg-secondary"}`}>
                      <Icon className={`h-4 w-4 ${isSelected ? "text-primary" : "text-muted-foreground"}`} />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground">{d.label}</h3>
                      <p className="text-[10px] text-muted-foreground">{d.affected}</p>
                    </div>
                  </div>
                  <p className="mb-3 text-xs leading-relaxed text-muted-foreground">{d.description}</p>
                  <div className="space-y-1">
                    <Progress value={p} className="h-1.5" />
                    <div className="flex items-center justify-between text-[10px] text-muted-foreground">
                      <span>${c.raised.toLocaleString()} raised</span>
                      <span className="flex items-center gap-1">
                        <Users className="h-2.5 w-2.5" />
                        {c.donors} donors
                      </span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Donation Card */}
          <Card className="border-border bg-card">
            <CardContent className="space-y-5 p-6">
              {/* Amount selector */}
              <div className="space-y-2">
                <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Choose Amount (USD)
                </label>
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                  {amounts.map((a) => (
                    <button
                      key={a}
                      onClick={() => { setSelectedAmount(a); setCustomAmount(""); }}
                      className={`rounded-lg border py-2.5 font-mono text-sm font-bold transition-all ${
                        selectedAmount === a && !customAmount
                          ? "border-primary bg-primary/10 text-primary"
                          : "border-border bg-secondary text-muted-foreground hover:border-primary/50"
                      }`}
                    >
                      ${a}
                    </button>
                  ))}
                </div>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 font-mono text-sm text-muted-foreground">$</span>
                  <input
                    type="number"
                    min={5}
                    placeholder="Custom amount"
                    value={customAmount}
                    onChange={(e) => setCustomAmount(e.target.value)}
                    className="w-full rounded-lg border border-border bg-secondary/50 py-2.5 pl-7 pr-3 font-mono text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
                  />
                </div>
              </div>

              {/* Impact statement */}
              {isValidAmount && (
                <div className="rounded-lg border border-primary/20 bg-primary/5 p-3">
                  <p className="text-xs leading-relaxed text-foreground">
                    <span className="font-semibold text-primary">${finalAmount}</span>
                    <ChevronRight className="mx-1 inline h-3 w-3 text-primary" />
                    {impactStatements[selectedAmount] || impactStatements[25]}
                  </p>
                </div>
              )}

              {/* Donate button */}
              <Button
                className="w-full gap-2 text-base font-semibold"
                size="lg"
                onClick={handleDonate}
                disabled={loading || !isValidAmount}
              >
                <HandHeart className="h-5 w-5" />
                {loading ? "Processing..." : `Donate $${finalAmount} Now`}
              </Button>

              {/* Trust signals */}
              <div className="space-y-2 pt-1">
                <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-4 text-[10px] text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Shield className="h-3 w-3" /> Secure via Stripe
                  </span>
                  <span className="hidden sm:inline">•</span>
                  <span>Tax receipt available</span>
                  <span className="hidden sm:inline">•</span>
                  <span>No recurring charges</span>
                </div>
                <p className="text-center text-[10px] text-muted-foreground">
                  Join {(cause.donors).toLocaleString()}+ donors who have contributed this month.
                  Minimum donation: $5 USD.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default DonationSection;
